/* ═══════════════════════════════════════════════════════════════════
   FREQUENTLY BOUGHT TOGETHER — CART DRAWER JS (Horizon theme)
   ═══════════════════════════════════════════════════════════════════
   Add this file at assets/fbt-cart.js.
   Load it as a plain script (no type="module" required anymore —
   this version no longer imports '@theme/events', which isn't
   available in every Horizon version):

     <script src="{{ 'fbt-cart.js' | asset_url }}"></script>

   Behavior: clicking the plus icon on an FBT item adds it to the cart
   IMMEDIATELY (no select-then-batch-add step). The button shows a
   loading state, then a check mark, then dispatches a `cart:update`
   event so Horizon's cart-drawer-component refreshes itself — which
   re-renders our Liquid block too, so the item that's now in cart
   drops out of FBT and a new companion may appear, per
   fbt-cart-trigger.liquid's logic.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  function setButtonState(btn, state) {
    // state: 'idle' | 'loading' | 'done' | 'error'
    btn.classList.remove('is-loading', 'is-done', 'is-error');
    var item = btn.closest('[data-fbt-item]');
    var status = item ? item.querySelector('[data-fbt-status]') : null;

    if (status) {
      status.classList.remove('fbt-cart-status--done', 'fbt-cart-status--error');
    }

    if (state === 'loading') {
      btn.classList.add('is-loading');
      btn.disabled = true;
      if (status) status.textContent = 'Adding…';
    } else if (state === 'done') {
      btn.classList.add('is-done');
      btn.disabled = true;
      if (status) {
        status.textContent = 'Item added to cart';
        status.classList.add('fbt-cart-status--done');
      }
    } else if (state === 'error') {
      btn.classList.add('is-error');
      btn.disabled = false;
      if (status) {
        status.textContent = 'Try again';
        status.classList.add('fbt-cart-status--error');
      }
    } else {
      btn.disabled = false;
      if (status) status.textContent = '';
    }
  }

  function getCurrentItemCount() {
    // component-cart-items.js already renders this hidden span with
    // the live count — reuse it instead of an extra /cart.js fetch.
    var el = document.querySelector('[ref="cartItemCount"]');
    var n = el ? parseInt(el.textContent, 10) : NaN;
    return isNaN(n) ? null : n;
  }

  function addItem(btn) {
    if (btn.disabled) return;
    var variantId = parseInt(btn.dataset.id, 10);
    if (!variantId) return;

    setButtonState(btn, 'loading');

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    // Hard timeout: if the network stalls, fail fast with a clear
    // "try again" instead of leaving the button stuck on "Adding…"
    // indefinitely.
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, 8000);

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      signal: controller.signal
    })
      .then(function (res) {
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        setButtonState(btn, 'done');
        var item = btn.closest('[data-fbt-item]');
        if (item) item.classList.add('fbt-cart-item--added');

        // Skips the extra /cart.js round trip — Horizon's own
        // cart-drawer-component re-fetches the real cart state when
        // it hears cart:update, so an estimated count here is only
        // used for the brief moment before that refresh lands.
        var currentCount = getCurrentItemCount();
        var estimatedCount = currentCount !== null ? currentCount + 1 : undefined;

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              data: {
                itemCount: estimatedCount,
                source: 'fbt-cart-widget'
              }
            }
          })
        );
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.error('[FBT Cart] Add timed out');
        } else {
          console.error('[FBT Cart] Add error:', err);
        }
        setButtonState(btn, 'error');
        setTimeout(function () { setButtonState(btn, 'idle'); }, 1500);
      });
  }

  function bindWidget(widget) {
    if (widget.dataset.fbtBound) return;
    widget.dataset.fbtBound = 'true';

    widget.querySelectorAll('.fbt-cart-plus-btn').forEach(function (btn) {
      btn.addEventListener('click', function () { addItem(btn); });
    });
  }

  function bindAll() {
    document.querySelectorAll('[data-fbt-widget]').forEach(bindWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  // Re-bind after any cart refresh, since Horizon re-renders the
  // Liquid block (fresh DOM nodes need fresh listeners).
  var cartEvents = ['cart:update', 'cart:refresh', 'cart:change'];
  cartEvents.forEach(function (evt) {
    document.addEventListener(evt, function () {
      setTimeout(bindAll, 400);
    });
  });
})();
